/**
 * src/lib/hooks/useStickyScroll.js — Task 1.4 (2026-04-21).
 *
 * Sticky-bottom auto-scroll pattern: auto-scrolls a scrollable
 * container to its last child when new items arrive, but suspends
 * auto-scroll while the user has manually scrolled up. Resumes when
 * the user scrolls back to within _STICKY_THRESHOLD px of the bottom.
 *
 * Design doc reference: §2.4 of
 *   docs/plans/2026-04-21-voice-rag-tiering-design.md
 *
 * Usage:
 *   const { containerRef, onScroll } = useStickyScroll([messages.length]);
 *   <div ref={containerRef} onScroll={onScroll}>...</div>
 *
 * The deps array is what the hook watches to trigger the scroll —
 * typically the length of the list being rendered.
 *
 * Wired into the voice overlay via TurnList. Also intentionally
 * generic so the chat composer can reuse it.
 */

import { useRef, useEffect, useCallback } from 'react';

export const _STICKY_THRESHOLD = 40;

export function useStickyScroll(deps = []) {
  const containerRef = useRef(null);
  const isStickyRef = useRef(true);

  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isStickyRef.current = distanceFromBottom <= _STICKY_THRESHOLD;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!isStickyRef.current) return;
    const last = el.lastElementChild;
    if (last && typeof last.scrollIntoView === 'function') {
      last.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- consumer-supplied deps govern when to re-scroll
  }, deps);

  return { containerRef, onScroll };
}
